/* istanbul ignore file */

import type { ValidationResult } from '../types';
import { isFiniteNumberString } from './numberStrings';

/**
 * Normalizes a validation path into a list of tokens.
 * Used by the validation engine when building/reading nested error-state objects.
 */
function getPathTokens(path: string): string[] {
    return path
        .replace(/\[(\d+)\]/g, '.$1')
        .split('.')
        .filter(Boolean);
}

/**
 * Converts numeric-looking tokens (e.g. "0") to a number key (0).
 * This keeps array-index segments consistent when walking nested structures.
 */
function coerceKey(token: string): string | number {
    return isFiniteNumberString(token) ? Number(token) : token;
}

/**
 * Returns whether a leaf node already exists at `path` in the error-state tree.
 * Used to avoid overwriting seeded defaults in `getFormErrorState()`.
 */
export function hasLeafAtPath(target: any, path: string): boolean {
    if (!target) return false;
    const tokens = getPathTokens(path);
    let cursor = target;

    for (let i = 0; i < tokens.length; i++) {
        const key = coerceKey(tokens[i]);
        const isLeaf = i === tokens.length - 1;
        if (cursor == null || typeof cursor !== 'object') return false;
        if (isLeaf) {
            return cursor[key] !== undefined;
        }
        cursor = cursor[key];
    }
    return false;
}

/**
 * Ensures a `{ hasError: false }` leaf exists at `path`.
 * Used by `getFormErrorState()` to seed a stable shape for known fields.
 */
export function setNestedDefault(target: any, path: string) {
    const tokens = getPathTokens(path);
    let cursor = target;

    for (let i = 0; i < tokens.length; i++) {
        const key = coerceKey(tokens[i]);
        const isLeaf = i === tokens.length - 1;

        if (isLeaf) {
            // Do not overwrite if already present
            if (cursor[key] === undefined) {
                cursor[key] = { hasError: false };
            }
        } else {
            if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {};
            cursor = cursor[key];
        }
    }
}

/**
 * Writes a leaf node at `path` based on the `ValidationResult` for the given channel.
 * Used by `getFormErrorState()` to overlay actual validation results onto the nested tree.
 */
export function setNestedError(target: any, path: string, result: ValidationResult, channel: keyof ValidationResult['channels']) {
    const tokens = getPathTokens(path);
    let cursor = target;

    for (let i = 0; i < tokens.length; i++) {
        const key = coerceKey(tokens[i]);
        const isLeaf = i === tokens.length - 1;

        if (isLeaf) {
            const message = (result.channels?.[channel] && result.channels[channel]?.[0]) || undefined;
            cursor[key] = { hasError: !result.valid, ...(message ? { errorMessage: message } : {}) };
        } else {
            if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {};
            cursor = cursor[key];
        }
    }
}

/**
 * Walks a nested error-state tree and returns true if any leaf has `hasError: true`.
 * Used by `getHasFormErrors()` as a quick boolean check.
 */
export function deepHasError(node: any): boolean {
    if (!node) return false;
    if (Array.isArray(node)) return node.some(deepHasError);
    if (typeof node === 'object') {
        if ('hasError' in node && typeof (node as any).hasError === 'boolean') {
            return (node as any).hasError as boolean;
        }
        return Object.values(node).some(deepHasError);
    }
    return false;
}
